import React, { useState } from 'react';
import { BadgeTypeModel, BadgeTypeCounterItem } from '../types';
import { api } from '../services/api';
import { Plus, Edit2, Trash2, KeyRound, X } from 'lucide-react';

interface BadgeTypesViewProps {
  badgeTypes: BadgeTypeModel[];
  counters: BadgeTypeCounterItem[];
  onRefresh: () => void;
  onOpenApiForType: (badgeTypeId: string) => void;
}

export const BadgeTypesView: React.FC<BadgeTypesViewProps> = ({
  badgeTypes,
  counters,
  onRefresh,
  onOpenApiForType,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBt, setEditingBt] = useState<BadgeTypeModel | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [color, setColor] = useState('#2563eb');
  const [saving, setSaving] = useState(false);

  const handleOpenAdd = () => {
    setEditingBt(null);
    setName('');
    setCode('');
    setColor('#2563eb');
    setIsModalOpen(true);
  };

  const handleOpenEdit = (bt: BadgeTypeModel) => {
    setEditingBt(bt);
    setName(bt.name);
    setCode(bt.code);
    setColor(bt.color);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editingBt) {
        await api.updateBadgeType(editingBt.id, { name, code, color });
      } else {
        await api.createBadgeType(name, code, color);
      }
      setIsModalOpen(false);
      onRefresh();
    } catch (err: any) {
      alert(`Error saving badge type: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bt: BadgeTypeModel) => {
    if (!confirm(`Delete badge type "${bt.name}"?`)) return;
    try {
      await api.deleteBadgeType(bt.id);
      onRefresh();
    } catch (err: any) {
      alert(`Error deleting badge type: ${err.message}`);
    }
  };

  const palette = ['#2563eb', '#059669', '#7c3aed', '#d97706', '#dc2626', '#0891b2', '#db2777'];

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Top Header */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-800">
        <div>
          <h1 className="text-lg font-bold text-white">Badge Types</h1>
          <p className="text-xs text-slate-400">
            Dynamic attendee categories. Each badge type receives its own counter and API.
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add Badge Type</span>
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-slate-900 border border-slate-800 overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-950/70 border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider text-[11px]">
            <tr>
              <th className="py-3 px-4">Badge Type Name</th>
              <th className="py-3 px-3">Code</th>
              <th className="py-3 px-3">Number of Badges</th>
              <th className="py-3 px-3">Number Scanned</th>
              <th className="py-3 px-3">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/80">
            {badgeTypes.map((bt) => {
              const counter = counters.find((c) => c.id === bt.id);
              const badgesCount = counter ? counter.totalBadges : 0;
              const scannedCount = counter ? counter.scannedCount : 0;

              return (
                <tr key={bt.id} className="hover:bg-slate-800/40 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: bt.color }} />
                      <span className="font-semibold text-white">{bt.name}</span>
                    </div>
                  </td>

                  <td className="py-3 px-3 font-mono font-medium text-slate-300">
                    {bt.code}
                  </td>

                  <td className="py-3 px-3 font-mono text-slate-200 tabular-nums">
                    {badgesCount}
                  </td>

                  <td className="py-3 px-3 font-mono font-bold text-emerald-400 tabular-nums">
                    {scannedCount}
                  </td>

                  <td className="py-3 px-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-500/10 text-emerald-400">
                      Active
                    </span>
                  </td>

                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => onOpenApiForType(bt.id)}
                        title="Generate or view API"
                        className="p-1.5 text-slate-400 hover:text-cyan-400 rounded hover:bg-slate-800 transition-colors"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleOpenEdit(bt)}
                        title="Edit Badge Type"
                        className="p-1.5 text-slate-400 hover:text-white rounded hover:bg-slate-800 transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(bt)}
                        title="Delete Badge Type"
                        className="p-1.5 text-slate-400 hover:text-rose-400 rounded hover:bg-slate-800 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl max-w-sm w-full p-5 space-y-4 shadow-xl">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <h2 className="text-sm font-bold text-white">
                {editingBt ? 'Edit Badge Type' : 'Add New Badge Type'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 text-xs">
              <div>
                <label className="text-slate-300 block mb-1 font-medium">Badge Type Name *</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Student, Exhibitor, VIP, Guest"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="text-slate-300 block mb-1 font-medium">Code (e.g. STD, EXH) *</label>
                <input
                  type="text"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. EXH"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white font-mono placeholder-slate-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-slate-300 block mb-1.5 font-medium">Color</label>
                <div className="flex items-center gap-2">
                  {palette.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`w-6 h-6 rounded-full border-2 transition-transform ${
                        color === c ? 'scale-110 border-white' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-800 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors shadow-sm disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingBt ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
